import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LayoutTemplate, Search, Download, ExternalLink, Sparkles, Loader2 } from "lucide-react";

const KINDS = [
  { k: "all", label: "All" },
  { k: "proposal", label: "Grant Proposals" },
  { k: "paper", label: "Papers" },
  { k: "thesis", label: "Theses" },
  { k: "travel", label: "Travel Grants" },
];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(""); const [kind, setKind] = useState("all");
  const [open, setOpen] = useState(false);
  const [gen, setGen] = useState({ title:"", kind:"proposal", fields:"" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/templates/library", { params: { ...(kind!=="all"&&{kind}), ...(q&&{q}) } }).then(r => setItems(r.data.items || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const generate = async () => {
    if (!gen.title.trim()) return toast.error("Give it a title");
    setBusy(true);
    try {
      const res = await fetch(`${API}/templates/generate`, {
        method:"POST", headers:{"Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("gf_token")}`},
        body: JSON.stringify(gen),
      });
      if (!res.ok) throw new Error("Generate failed");
      const blob = await res.blob(); const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`${gen.title.replace(/\s/g,"_")}.pdf`; a.click();
      toast.success("Template generated"); setOpen(false);
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline">Library</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><LayoutTemplate className="w-10 h-10 text-primary"/>Templates</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">A curated library of the world's most-used academic templates — grants, papers, theses, and travel grant applications — plus AI-generated custom templates.</p>
        </div>
        <Button className="rounded-full" onClick={()=>setOpen(true)} data-testid="tmpl-generate-btn"><Sparkles className="w-4 h-4 mr-2"/>Generate custom</Button>
      </div>

      <div className="card-crisp rounded-lg p-5 mb-6 flex gap-3 items-end">
        <div className="flex-1"><Label>Search</Label><Input placeholder="Search by title, discipline, format…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&load()} data-testid="tmpl-search"/></div>
        <Button variant="outline" onClick={load} className="rounded-full"><Search className="w-4 h-4 mr-2"/>Search</Button>
      </div>

      <Tabs value={kind} onValueChange={setKind} className="mb-6">
        <TabsList>{KINDS.map(k => <TabsTrigger key={k.k} value={k.k} data-testid={`tmpl-tab-${k.k}`}>{k.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(it => (
          <div key={it.title} className="card-crisp rounded-lg p-5 hover:border-primary/60" data-testid={`tmpl-item-${it.title.slice(0,15)}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><LayoutTemplate className="w-5 h-5"/></div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-secondary text-muted-foreground">{it.kind}</span>
            </div>
            <div className="font-display text-xl mt-3 leading-tight line-clamp-2">{it.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{it.discipline} · {it.format}</div>
            <div className="text-sm text-muted-foreground mt-3 line-clamp-3">{it.summary}</div>
            <div className="mt-4 flex gap-2">
              {it.url && <a href={it.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full"><ExternalLink className="w-3.5 h-3.5 mr-1"/>Open source</Button></a>}
              <Button size="sm" className="rounded-full" onClick={()=>{ setGen({ title: it.title, kind: it.kind, fields: it.summary }); setOpen(true); }}><Sparkles className="w-3.5 h-3.5 mr-1"/>Adapt with AI</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Generate a custom template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kind</Label>
              <select className="w-full h-10 border border-border rounded-md px-3 bg-transparent" value={gen.kind} onChange={(e)=>setGen({...gen, kind:e.target.value})} data-testid="gen-kind">
                <option value="proposal">Grant Proposal</option><option value="paper">Research Paper</option><option value="thesis">Thesis</option><option value="travel">Travel Grant</option>
              </select>
            </div>
            <div><Label>Title</Label><Input value={gen.title} onChange={(e)=>setGen({...gen, title:e.target.value})} data-testid="gen-title"/></div>
            <div><Label>Fields you want</Label><Input value={gen.fields} onChange={(e)=>setGen({...gen, fields:e.target.value})} placeholder="e.g., Overview, Objectives, Budget, Timeline"/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={generate} disabled={busy} data-testid="gen-download">{busy?<Loader2 className="w-4 h-4 mr-2 animate-spin"/>:<Download className="w-4 h-4 mr-2"/>}Download PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
